import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ScButton from "@/src/components/ui-kit/button";
import { isRealOrgId } from "@/src/constants/roles-permissions.constants";
import { useAuth } from "@/src/contexts/auth.context";
import { ResolvedOrg } from "@/src/models/permissions.model";
import {
  getRefreshToken,
  getToken,
  useLogoutMutation,
} from "@/src/services/api/auth.api";
import { useGetOrganizationColorQuery } from "@/src/services/organization/organization.hooks";
import {
  useOrganizationsQuery,
  useSwitchOrgMutation,
} from "@/src/services/uam/permissions.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import { derivePalette } from "@/src/utils/derive-palette";
import { formatRoleLabel, getPrimaryRole } from "@/src/utils/role.utils";
import { useLocalSearchParams, useRouter } from "expo-router";
import { LockCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, Pressable, Text, View } from "react-native";
import Toast from "react-native-toast-message";

function OrgSelectSkeleton() {
  return (
    <View className="flex-1 bg-obsidian items-center justify-center p-6">
      <ActivityIndicator size="large" />
    </View>
  );
}

export default function OrgSelectScreen() {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user, appRole, refreshAuth, logout } = useAuth();
  const router = useRouter();
  const { setPrimaryPalette } = useTheme();

  // CR-B2-02: useLocalSearchParams MUST be above every conditional return to satisfy
  // rules-of-hooks. Previously called after the isLoading skeleton return (line 146
  // in the pre-fix file), which caused a rules-of-hooks violation and render jank.
  const { pending } = useLocalSearchParams<{ pending?: string }>();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const { mutateAsync: logoutMutateAsync } = useLogoutMutation();
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const refreshToken = await getRefreshToken();
      const accessToken = await getToken();

      if (refreshToken && accessToken) {
        await logoutMutateAsync({ refreshToken, accessToken });
      }
    } catch {
      // proceed with local logout even if the API call fails
    } finally {
      await logout();
      setIsLoggingOut(false);
    }
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await handleLogout();
  };

  const { mutateAsync: switchOrg } = useSwitchOrgMutation();
  const { data: organizationsData, isLoading: isOrgsLoading } =
    useOrganizationsQuery();

  /**
   * The picker is driven by `GET /iam/organizations/my`, NOT by
   * `user.memberships`.
   *
   * `memberships` is built from `/iam/v4/iam/me`, which reports only the
   * *session's* `organizationId` and carries no `organizationIds` field at all.
   * On a session that has not switched org yet that value is `default`, so
   * `memberships` is empty — and iterating it rendered an empty list under the
   * "Select Organization" heading, with no error and no empty state. Same root
   * cause as the `?pending=true` lockout; see the `realOrgs` note in auth.context.
   *
   * A missing role no longer drops the row either. The role is a subtitle, and
   * `appRoles` deliberately strips `clouduser` — so a user whose only role in the
   * current org is a platform one resolved to no role and had their org silently
   * removed from a screen whose entire purpose is to list it.
   */
  const resolvedOrgs: ReadonlyArray<ResolvedOrg> = (organizationsData ?? [])
    .filter((org) => isRealOrgId(org.itemId))
    .map((org) => {
      const membership = user?.memberships?.find(
        (m) => m.organizationId === org.itemId,
      );
      // `getPrimaryRole` strips `clouduser` and prefers super-admin/admin over an
      // incidental first entry. It returns "" rather than a placeholder, so each
      // fallback is reached only when the previous source genuinely has no role.
      const role =
        getPrimaryRole(membership?.roles) ||
        getPrimaryRole(user?.roles) ||
        appRole ||
        "";

      return {
        organizationId: org.itemId,
        orgName: org.name ?? "",
        role: formatRoleLabel(role),
      };
    });

  const { data: orgColor, refetch: refetchOrgColor } =
    useGetOrganizationColorQuery({ enabled: false });

  // CR-B2-02: navigate immediately after session is switched and refreshed.
  // Org colour fetch is fire-and-forget — a slow/failing colour call must not delay
  // navigation (AC-B2-02-04). The palette still applies once the fetch resolves.
  const handleSelectOrg = async (orgId: string) => {
    setSwitchingOrgId(orgId);
    try {
      const refreshToken = (await getRefreshToken()) ?? "";
      if (Platform.OS !== "web" && !refreshToken)
        throw new Error("Missing refresh token");

      await switchOrg({ orgId, refreshToken });

      // Must NOT pass skipRedirect. `refreshAuth(skipRedirect)` only re-arms
      // AuthProvider's one-shot `hasRedirected` guard when the flag is absent,
      // and that guard was already spent by the post-login redirect that sent
      // this user here in the first place.
      //
      // Leaving it spent deadlocked newly invited users: the replace below can
      // land on `/`, and `app/index.tsx` deliberately renders nothing but a
      // spinner because AuthProvider is "the single authority for where to
      // land" — so the screen waited for a redirect that could never fire
      // again. `hasRedirected` is a ref, so only a full page reload cleared it,
      // which is exactly the reported workaround.
      //
      // Re-arming makes AuthProvider route once more off the refreshed session,
      // which also self-corrects the destination below (computed from the
      // pre-switch `user`, since refreshAuth clears the query cache).
      await refreshAuth();

      // Navigate immediately — do not await the colour fetch (AC-B2-02-04)
      const hasName = Boolean(user?.firstName?.trim() || user?.lastName?.trim());
      router.replace(
        !hasName
          ? `/profile/${user?.itemId}/edit?onboarding=true`
          : "/feeds",
      );

      // Apply org theme after navigation (non-blocking)
      refetchOrgColor()
        .then(({ data: colorData }) => {
          const hex = colorData?.primaryColor
            ? String(colorData.primaryColor).trim()
            : null;
          if (hex) {
            try {
              setPrimaryPalette(derivePalette(hex));
            } catch {
              /* palette derivation failure is cosmetic */
            }
          }
        })
        .catch(() => {/* colour fetch failure is non-blocking */});
    } catch {
      // AC-B2-02-05: switchOrg error — show message and leave user on picker
      Toast.show({ type: "error", text1: t("ERROR_SWITCH_ORG_FAILED") });
    } finally {
      setSwitchingOrgId(null);
    }
  };

  // CR-B2-02: only show the full-screen skeleton while the org list is loading.
  // isSwitchOrgPending is no longer included — per-row feedback replaces the
  // full-screen spinner for the switch operation (AC-B2-02-03).
  if (isOrgsLoading) return <OrgSelectSkeleton />;

  // An empty list is the same state as `?pending=true` — the user has no org to
  // pick — so show that instead of the heading above an empty box. Rendering the
  // picker chrome with nothing under it is what "Select Organization" over blank
  // space was, and it gave no indication whether the list was empty or broken.
  if (pending === "true" || resolvedOrgs.length === 0) {
    return (
      <>
        <View className="flex-1 bg-obsidian items-center justify-center gap-4 p-8">
          <LockCircle size={64} color={"var(--color-ash-1)"} />
          <Text className="text-xl font-semibold text-center text-onyx">
            {t("LABEL_PENDING_ORG_ASSIGNMENT")}
          </Text>
          <Text className="text-slate text-center text-sm">
            {t("LABEL_PENDING_ORG_ASSIGNMENT_DESC")}
          </Text>
          <ScButton
            text="BTN_LOGOUT"
            mode="outlined"
            onPress={() => {
              setShowLogoutModal(true);
            }}
          />
        </View>
        <ConfirmModal
          visible={showLogoutModal}
          title="LABEL_LOGOUT_CONFIRM"
          description="HINT_LOGOUT_CONFIRM"
          confirmText={t("BTN_LOGOUT")}
          cancelText={t("BTN_CANCEL")}
          loading={isLoggingOut}
          onCancel={() => setShowLogoutModal(false)}
          onConfirm={handleConfirmLogout}
        />
      </>
    );
  }

  return (
    <View className="flex-1 bg-obsidian items-center justify-center p-6">
      <Text className="text-2xl font-bold text-onyx mb-2">
        {t("LABEL_SELECT_ORGANIZATION")}
      </Text>
      <Text className="text-sm text-slate mb-8 text-center">
        {t("HINT_SELECT_ORGANIZATION")}
      </Text>

      <View className="w-full max-w-sm gap-3">
        {resolvedOrgs.map((org) => {
          const isSwitching = switchingOrgId === org.organizationId;
          return (
            <Pressable
              key={org.organizationId}
              onPress={() => handleSelectOrg(org.organizationId)}
              disabled={switchingOrgId !== null}
              className="bg-chalk border border-ash-3 rounded-xl p-4 active:opacity-80 hover:bg-ash-3"
            >
              <View className="flex-row items-center justify-between">
                <View className="flex-1">
                  <Text className="font-semibold text-onyx text-base">
                    {org?.orgName}
                  </Text>
                  <Text className="text-xs text-slate mt-0.5">{org?.role}</Text>
                </View>
                {isSwitching && (
                  <ActivityIndicator size="small" className="ml-2" />
                )}
              </View>
            </Pressable>
          );
        })}
      </View>

      <ConfirmModal
        visible={showLogoutModal}
        title="LABEL_LOGOUT_CONFIRM"
        description="HINT_LOGOUT_CONFIRM"
        confirmText={t("BTN_LOGOUT")}
        cancelText={t("BTN_CANCEL")}
        loading={isLoggingOut}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </View>
  );
}
